/**
 * Sync Service
 * Processes the offline sync queue: submits pending evaluations to the server
 * and handles audio transcription when back online.
 */
import {
  getSyncQueue,
  updateSyncQueueItem,
  removeSyncQueueItem,
  deleteDraft,
  deleteAudioRecordingsForDraft,
  getUntranscribedRecordings,
  updateRecordingTranscription,
  deleteAudioRecording,
  isLocalProspectUID,
  replaceDraftProspectUID,
  replaceQueuedProspectReferences,
  updateAudioRecordingsDraftKey,
  isValidUID,
  sanitizeUID,
} from './offlineStorage.service';
import { EvaluationCategoryData } from './Evaluation.service';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';

let isSyncing = false;
let syncListeners = [];

function notifySyncListeners(event) {
  syncListeners.forEach((fn) => {
    try { fn(event); } catch { /* ignore */ }
  });
}

/**
 * Subscribe to sync events.
 * Events: { type: 'start'|'progress'|'complete'|'error'|'item-synced'|'item-failed', data }
 */
export function subscribeSyncEvents(listener) {
  syncListeners.push(listener);
  return () => {
    syncListeners = syncListeners.filter((fn) => fn !== listener);
  };
}

/**
 * Process the sync queue: submit all pending evaluations to the server.
 * Processes sequentially (FIFO) to avoid conflicts.
 * @returns {Promise<{ synced: number, failed: number, prospectsSynced: number, prospectsFailed: number, syncedDraftKeys: string[] }>}
 */
function sortSyncQueue(queue) {
  return [...queue].sort((left, right) => {
    const leftPriority = left.itemType === 'prospect' ? 0 : 1;
    const rightPriority = right.itemType === 'prospect' ? 0 : 1;
    if (leftPriority !== rightPriority) {
      return leftPriority - rightPriority;
    }
    return (left.addedAt || 0) - (right.addedAt || 0);
  });
}


/**
 * Extract and validate official UID from server response.
 * SECURITY: Validates untrusted server data before using it.
 * @param {Object} result - The server response
 * @returns {string|null} - Valid UID or null if invalid
 */
function extractOfficialUID(result) {
  let uid = result?.officialUID
    || result?.OfficialUID
    || result?.id
    || result?.data?.officialUID
    || result?.data?.OfficialUID
    || result?.data?.id
    || null;

  if (!uid) {
    return null;
  }

  // Convert to string if numeric
  uid = String(uid);

  // SECURITY: Validate UID format to prevent injection attacks
  if (!isValidUID(uid)) {
    console.warn('Server returned invalid UID format, attempting sanitization:', { uid });
    const sanitized = sanitizeUID(uid);
    if (!sanitized) {
      console.error('UID sanitization failed, UID rejected:', { uid });
      return null;
    }
    return sanitized;
  }

  return uid;
}

function replaceOfficialUIDInMemory(item, oldOfficialUID, newOfficialUID, draftKeyChanges = []) {
  if (!item || item.itemType !== 'evaluation' || item.payload?.officialUID !== oldOfficialUID) {
    return item;
  }

  const draftKeyMap = new Map(draftKeyChanges.map(({ oldDraftKey, newDraftKey }) => [oldDraftKey, newDraftKey]));
  return {
    ...item,
    draftKey: draftKeyMap.get(item.draftKey) || item.draftKey,
    payload: {
      ...item.payload,
      officialUID: newOfficialUID,
      evaluationRatingList: (item.payload.evaluationRatingList || []).map((rating) => ({
        ...rating,
        officialUID: newOfficialUID,
      })),
      evaluationRatingCompetencyList: (item.payload.evaluationRatingCompetencyList || []).map((competency) => ({
        ...competency,
        officialUID: newOfficialUID,
      })),
    },
  };
}

export async function processSyncQueue() {
  if (isSyncing) return { synced: 0, failed: 0, prospectsSynced: 0, prospectsFailed: 0, syncedDraftKeys: [] };
  isSyncing = true;
  notifySyncListeners({ type: 'start' });

  const queue = sortSyncQueue(getSyncQueue().filter((item) => item.status === 'pending' || item.status === 'failed'));
  let synced = 0;
  let failed = 0;
  let prospectsSynced = 0;
  let prospectsFailed = 0;
  // Draft keys of evaluation items that were pushed to the server in this run.
  // Consumers use this to reset the on-screen form when the draft it is showing
  // has just been synced, preventing a stale copy from being re-saved as a
  // duplicate record.
  const syncedDraftKeys = [];

  for (let i = 0; i < queue.length; i++) {
    let item = queue[i];
    notifySyncListeners({ type: 'progress', data: { current: i + 1, total: queue.length, item } });

    updateSyncQueueItem(item.id, { status: 'syncing', lastAttempt: Date.now() });

    try {
      if (item.itemType === 'prospect') {
        // The server-side insert-prospect-user endpoint now returns the existing user
        // record when the email is already taken, so a single call is always sufficient.
        const result = await EvaluationCategoryData.insertEvalProspect(item.payload);
        const officialUID = extractOfficialUID(result);

        if (!officialUID) {
          throw new Error('Prospect sync succeeded but no official UID was returned.');
        }

        const draftKeyChanges = replaceDraftProspectUID(item.localProspectUID, officialUID);
        await Promise.all(draftKeyChanges.map(({ oldDraftKey, newDraftKey }) => updateAudioRecordingsDraftKey(oldDraftKey, newDraftKey)));
        replaceQueuedProspectReferences(item.localProspectUID, officialUID, draftKeyChanges);

        for (let j = i + 1; j < queue.length; j++) {
          queue[j] = replaceOfficialUIDInMemory(queue[j], item.localProspectUID, officialUID, draftKeyChanges);
        }

        removeSyncQueueItem(item.id);
        prospectsSynced++;
        notifySyncListeners({
          type: 'item-synced',
          data: { item: { ...item, syncedOfficialUID: officialUID } },
        });
        continue;
      }

      if (isLocalProspectUID(item.payload?.officialUID)) {
        throw new Error('Evaluation is waiting for its offline-created prospect to sync first.');
      }

      await EvaluationCategoryData.saveEvaluationRatings(item.payload);
      removeSyncQueueItem(item.id);
      if (item.draftKey) {
        deleteDraft(item.draftKey);
        await deleteAudioRecordingsForDraft(item.draftKey);
        syncedDraftKeys.push(item.draftKey);
      }
      synced++;
      notifySyncListeners({ type: 'item-synced', data: { item } });
    } catch (error) {
      if (item.itemType === 'prospect') {
        prospectsFailed++;
      } else {
        failed++;
      }
      updateSyncQueueItem(item.id, {
        status: 'failed',
        retryCount: (item.retryCount || 0) + 1,
        lastError: error.message || 'Unknown error',
      });
      notifySyncListeners({ type: 'item-failed', data: { item, error: error.message } });
    }
  }

  isSyncing = false;
  notifySyncListeners({ type: 'complete', data: { synced, failed, prospectsSynced, prospectsFailed, syncedDraftKeys } });
  return { synced, failed, prospectsSynced, prospectsFailed, syncedDraftKeys };
}

/**
 * Transcribe all untranscribed audio recordings using Azure Cognitive Services.
 * Returns an array of { recordId, fieldName, draftKey, transcription } results.
 * @param {Function} onProgress - Optional callback(current, total)
 * @returns {Promise<Array>}
 */
export async function transcribeAllRecordings(onProgress) {
  const recordings = await getUntranscribedRecordings();

  console.group('[STT] transcribeAllRecordings');
  console.log('Recordings found in IndexedDB:', recordings.length);
  recordings.forEach((r, i) => {
    console.log(`  [${i}] id=${r.id} field="${r.fieldName}" draftKey="${r.draftKey}" mimeType="${r.mimeType}" blobSize=${r.audioBlob?.size ?? 'N/A'} transcribed=${r.transcribed}`);
  });

  if (recordings.length === 0) {
    console.warn('  No untranscribed recordings found — nothing to do.');
    console.groupEnd();
    return [];
  }

  // Get the speech token once
  let tokenData;
  try {
    tokenData = await EvaluationCategoryData.getCognitiveServiceToken();
    console.log('Token fetched:', {
      useKey: tokenData?.useKey,
      region: tokenData?.region,
      hasToken: !!tokenData?.token,
      hasEndpoint: !!tokenData?.endpoint,
      hasSubscriptionKey: !!tokenData?.subscriptionKey,
    });
  } catch (error) {
    console.error('Failed to get speech token:', error);
    console.groupEnd();
    throw new Error('Failed to get speech token: ' + error.message);
  }

  const results = [];

  for (let i = 0; i < recordings.length; i++) {
    const rec = recordings[i];
    if (onProgress) onProgress(i + 1, recordings.length);

    console.group(`  [${i}] Transcribing recording id=${rec.id} field="${rec.fieldName}"`);
    try {
      const transcription = await transcribeSingleRecording(rec.audioBlob, tokenData, rec.mimeType);
      console.log('  Result:', JSON.stringify(transcription));
      await updateRecordingTranscription(rec.id, transcription);
      results.push({
        recordId: rec.id,
        fieldName: rec.fieldName,
        draftKey: rec.draftKey,
        transcription,
      });
    } catch (error) {
      console.error('  FAILED:', error.message, error);
      results.push({
        recordId: rec.id,
        fieldName: rec.fieldName,
        draftKey: rec.draftKey,
        transcription: null,
        error: error.message,
      });
    }
    console.groupEnd();
  }

  console.log('All done. Results:', results);
  console.groupEnd();
  return results;
}

/**
 * Decode a compressed audio Blob (WebM/Opus, OGG/Opus, etc.) to a PCM WAV ArrayBuffer
 * using the browser's built-in Web Audio API decoder.
 * Returns { wav: ArrayBuffer, sampleRate: number }.
 */
async function decodeAudioBlobToPcmWav(audioBlob) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) throw new Error('Web Audio API is not supported in this browser');

  const audioContext = new AudioCtx();
  try {
    const inputBuffer = await audioBlob.arrayBuffer();
    // decodeAudioData decodes any format the browser supports:
    //   Chrome/Firefox: WebM/Opus, MP3, MP4/AAC, WAV
    //   Safari/iOS:     MP4/AAC, MP3, WAV  (no WebM — AudioRecorder uses audio/mp4 on Safari)
    let audioBuffer;
    try {
      audioBuffer = await audioContext.decodeAudioData(inputBuffer);
    } catch (decodeErr) {
      throw new Error(
        `Browser could not decode audio (type="${audioBlob.type}", size=${audioBlob.size}). ` +
        `Safari does not support WebM — ensure the recording was made on this device. ` +
        `Original error: ${decodeErr.message}`
      );
    }

    const sampleRate = audioBuffer.sampleRate;
    // Take first channel (convert to mono)
    const samples = audioBuffer.getChannelData(0);
    const wav = encodeWav(samples, sampleRate);
    return { wav, sampleRate };
  } finally {
    audioContext.close();
  }
}

/** Encode a Float32Array of mono PCM samples as a 16-bit WAV ArrayBuffer. */
function encodeWav(samples, sampleRate) {
  const dataBytes = samples.length * 2; // 16-bit = 2 bytes per sample
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);

  const str = (offset, text) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
  };

  str(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  str(8, 'WAVE');
  str(12, 'fmt ');
  view.setUint32(16, 16, true);           // PCM chunk size
  view.setUint16(20, 1, true);            // PCM format
  view.setUint16(22, 1, true);            // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate (mono 16-bit)
  view.setUint16(32, 2, true);            // block align
  view.setUint16(34, 16, true);           // bits per sample
  str(36, 'data');
  view.setUint32(40, dataBytes, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    offset += 2;
  }
  return buffer;
}

/**
 * Transcribe a single audio blob using Azure Speech SDK.
 * @param {Blob} audioBlob
 * @param {Object} tokenData - { token, region, endpoint, subscriptionKey, useKey }
 * @param {string} savedMimeType - mimeType saved with the IndexedDB record
 * @returns {Promise<string>} Transcribed text
 */
async function transcribeSingleRecording(audioBlob, tokenData, savedMimeType) {
  return new Promise(async (resolve, reject) => {
    try {
      let speechConfig;

      if (tokenData.useKey && tokenData.endpoint) {
        const cleanEndpoint = tokenData.endpoint.replace(/\/$/, '');
        const endpointUrl = new URL(`${cleanEndpoint}/stt/speech/recognition/conversation/cognitiveservices/v1`);
        endpointUrl.searchParams.set('language', 'en-US');
        console.log('    SpeechConfig: fromEndpoint (useKey)', endpointUrl.toString());
        speechConfig = SpeechSDK.SpeechConfig.fromEndpoint(endpointUrl, tokenData.subscriptionKey);
      } else if (tokenData.endpoint) {
        const endpointUrl = new URL(tokenData.endpoint.replace(/\/$/, ''));
        console.log('    SpeechConfig: fromEndpoint (token)', endpointUrl.toString());
        speechConfig = SpeechSDK.SpeechConfig.fromEndpoint(endpointUrl, '');
        speechConfig.authorizationToken = tokenData.token;
      } else {
        console.log('    SpeechConfig: fromAuthorizationToken, region=', tokenData.region);
        speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(tokenData.token, tokenData.region);
      }

      speechConfig.speechRecognitionLanguage = 'en-US';
      speechConfig.outputFormat = SpeechSDK.OutputFormat.Detailed;
      speechConfig.setProperty("speechContext-PhraseDetection.DisfluencyRemoval", "true");
      speechConfig.setServiceProperty("punctuation", "explicit", SpeechSDK.ServicePropertyChannel.UriQueryParameter);
      speechConfig.enableDictation();

      console.log('    Blob info:', { type: audioBlob.type, savedMimeType, size: audioBlob.size });

      // Decode compressed audio (WebM/Opus) → PCM WAV via the browser's Web Audio API.
      // Feeding raw WebM bytes to the SDK's compressed push stream is unreliable across
      // browser/SDK version combinations. Decoding first and using a plain PCM push
      // stream is the most robust approach.
      console.log('    Decoding audio to PCM WAV...');
      const { wav: wavArrayBuffer, sampleRate } = await decodeAudioBlobToPcmWav(audioBlob);
      console.log('    WAV ready:', { byteLength: wavArrayBuffer.byteLength, sampleRate });

      const streamFormat = SpeechSDK.AudioStreamFormat.getWaveFormatPCM(sampleRate, 16, 1);
      const pushStream = SpeechSDK.AudioInputStream.createPushStream(streamFormat);
      pushStream.write(wavArrayBuffer);
      pushStream.close();

      const audioConfig = SpeechSDK.AudioConfig.fromStreamInput(pushStream);
      const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);

      // Add domain phrases
      const phraseListGrammar = SpeechSDK.PhraseListGrammar.fromRecognizer(recognizer);
      const domainPhrases = [
        "foul", "personal foul", "technical foul", "flagrant foul",
        "traveling", "double dribble", "shot clock violation",
        "mechanics", "positioning", "court presence", "communication",
        "needs improvement", "below expectations", "meets expectations",
        "above expectations", "exceptional", "outstanding",
      ];
      domainPhrases.forEach((phrase) => phraseListGrammar.addPhrase(phrase));

      // Use continuous recognition to handle longer recordings
      let fullText = '';

      recognizer.recognizing = (s, e) => {
        console.log('    [recognizing]', e.result.text);
      };

      recognizer.recognized = (s, e) => {
        console.log('    [recognized] reason:', e.result.reason, 'text:', e.result.text);
        if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          const text = e.result.text || '';
          if (text) {
            fullText += (fullText ? ' ' : '') + text;
          }
        } else if (e.result.reason === SpeechSDK.ResultReason.NoMatch) {
          console.warn('    [recognized] NoMatch — no speech detected in this segment.');
        }
      };

      recognizer.canceled = (s, e) => {
        console.log('    [canceled] reason:', e.reason, 'errorCode:', e.errorCode, 'errorDetails:', e.errorDetails);
        if (e.reason === SpeechSDK.CancellationReason.EndOfStream) {
          console.log('    EndOfStream — finishing with fullText:', JSON.stringify(fullText));
          recognizer.stopContinuousRecognitionAsync(
            () => { recognizer.close(); resolve(fullText); },
            (err) => { console.warn('    stopContinuousRecognitionAsync error (ignored):', err); recognizer.close(); resolve(fullText); }
          );
        } else if (e.reason === SpeechSDK.CancellationReason.Error) {
          const msg = `Azure STT error — code: ${e.errorCode}, details: ${e.errorDetails}`;
          console.error('    [canceled/Error]', msg);
          recognizer.close();
          reject(new Error(msg));
        } else {
          console.warn('    [canceled] unexpected reason:', e.reason);
          recognizer.close();
          resolve(fullText);
        }
      };

      recognizer.sessionStarted = () => console.log('    [sessionStarted]');

      recognizer.sessionStopped = () => {
        console.log('    [sessionStopped] fullText:', JSON.stringify(fullText));
        recognizer.close();
        resolve(fullText);
      };

      console.log('    Starting continuous recognition...');
      recognizer.startContinuousRecognitionAsync(
        () => { console.log('    startContinuousRecognitionAsync: started ✓'); },
        (err) => { console.error('    startContinuousRecognitionAsync failed:', err); recognizer.close(); reject(new Error(err)); }
      );
    } catch (error) {
      console.error('    Unexpected error in transcribeSingleRecording:', error);
      reject(error);
    }
  });
}

/**
 * Delete a transcribed audio recording after user confirms.
 * @param {number} recordId
 */
export async function confirmAndDeleteRecording(recordId) {
  await deleteAudioRecording(recordId);
}

export function isSyncInProgress() {
  return isSyncing;
}
